import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MapPinIcon,
  HeartIcon as HeartOutline,
  FireIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import FadeInImage from '../FadeInImage';

/**
 * Premium event/experience card.
 *
 * Used by:
 *   - Home → Featured experiences
 *   - Home → Events / Activities / Stays carousels (later)
 *   - Events listing page (later)
 *
 * Required event fields (from /api/events* + /api/hot-events):
 *   id, slug, title, image, citie, type, prices[{price, default}],
 *   subtotal_reviews, total_reviews, is_hot
 */

function defaultPrice(event) {
  const list = event?.prices || [];
  if (!list.length) return null;
  const def = list.find((p) => p.default === 1 || p.default === '1');
  return (def || list[0]).price;
}

function rating(event) {
  const total = event?.total_reviews || 0;
  if (!total) return null;
  // Display rule: if a product has any reviews, show 4.9. The raw subtotal/total
  // math in the DB is unreliable (subtotal_reviews is often 0 even when 5 reviews
  // exist), so we don't want to surface "0.0 (5 reviews)" on the homepage.
  return '4.9';
}

function typeLabel(type, t) {
  if (type === 'Event') return t('hero.typeTrip', 'Voyage');
  if (type === 'Activitie') return t('hero.typeActivity', 'Activité');
  if (type === 'Sejour') return t('hero.typeStay', 'Séjour');
  return type;
}

// Inline chevron — used for the round arrow button + view-all links
function ChevronRight(props) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
      {...props}
    >
      <path
        fillRule="evenodd"
        d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export default function EventCard({ event, width = 288, className = '' }) {
  const { t } = useTranslation();
  const price = defaultPrice(event);
  const stars = rating(event);
  const reviewsN = event?.total_reviews || 0;
  const isNew = !stars;

  return (
    <Link
      to={`/event/${event.id}`}
      className={`group relative flex shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card transition-all duration-300 ease-out-quint hover:-translate-y-1 hover:border-brand-100 hover:shadow-elevated ${className}`}
      style={width ? { width: `${width}px` } : undefined}
    >
      {/* image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <div className="h-full w-full transition-transform duration-[900ms] ease-out-quint group-hover:scale-[1.06]">
          <FadeInImage
            src={event.image}
            alt={event.title}
            className="h-full w-full object-cover"
          />
        </div>

        {/* gradient at top for chip legibility */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/30 to-transparent" />

        {/* top row: type chip (left) + status badge (right) */}
        <div className="absolute inset-x-3 top-3 flex items-start justify-between gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand-900 shadow-card backdrop-blur">
            {typeLabel(event.type, t)}
          </span>
          {event.is_hot ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-semibold text-brand-900 shadow-card">
              <FireIcon className="h-3 w-3" />
              {t('home.featuredHot', 'Hot')}
            </span>
          ) : isNew ? (
            <span className="inline-flex items-center rounded-full bg-brand-500 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white shadow-card">
              {t('home.featuredNoReviews', 'New')}
            </span>
          ) : null}
        </div>

        {/* heart save button */}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          aria-label="Save"
          className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-ink-muted shadow-card transition-all duration-200 hover:scale-110 hover:text-rose-500 rtl:right-auto rtl:left-3"
        >
          <HeartOutline className="h-4 w-4" />
        </button>
      </div>

      {/* body */}
      <div className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-2 font-display text-base font-semibold leading-snug text-ink transition-colors group-hover:text-brand-500">
          {event.title}
        </h3>

        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-ink-muted">
          <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-brand-500" />
          <span className="truncate">{event.citie}</span>
        </div>

        {/* Reviews row — only when the event has reviews */}
        {stars ? (
          <div className="mt-1 flex items-center gap-1.5 text-xs">
            <StarIcon className="h-3.5 w-3.5 text-accent-500" />
            <span className="font-semibold text-ink">{stars}</span>
            <span className="text-ink-subtle">({reviewsN} {t('home.featuredReviews', 'avis')})</span>
          </div>
        ) : null}

        {/* price row — pinned to the bottom of the card */}
        <div className="mt-auto flex items-end justify-between gap-2 border-t border-slate-100 pt-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-ink-subtle">
              {t('home.featuredFrom', 'From')}
            </p>
            <p className="font-display text-lg font-bold leading-none text-brand-500">
              {price ? (
                <>
                  <bdi>{price}</bdi> {t('home.featuredCurrency', 'MAD')}
                </>
              ) : '—'}
            </p>
            <p className="mt-0.5 text-[10px] text-ink-subtle">
              {t('home.featuredPerPerson', '/ person')}
            </p>
          </div>
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white transition-all duration-200 group-hover:bg-accent-500 group-hover:text-brand-900">
            <ChevronRight className="h-5 w-5 rtl:rotate-180" />
          </span>
        </div>
      </div>
    </Link>
  );
}
